import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Audio, AudioStatusReasonType, AudioStatusType } from 'types/audio';
import { Member } from 'types/splits.types';
import { _SongLinks, Song } from 'types/song';
import { LoadingState } from 'types/enums';
import { onlyUnique } from 'app/utils/utils';
import {
  AudioUpdatedPayload,
  SongFolderChangedPayload,
} from 'types/websocket.types';
import {
  acceptSplitEffect,
  createFolderEffect,
  createSongEffect,
  createSplitEffect,
  deleteFolderEffect,
  deleteSongAudioEffect,
  deleteSongContributionEffect,
  deleteSongContributorEffect,
  updateFolderEffect,
  fetchContributionClassesRolesDDLEffect,
  fetchContributionsListEffect,
  fetchCreateContributionValidationsEffect,
  fetchFoldersEffect,
  fetchLanguageCodesDDLEffect,
  fetchLatestSplitEffect,
  fetchPossibleSplitMembersEffect,
  fetchSongAudioEffect,
  fetchSongAudioListEffect,
  fetchSongAudioTypesEffect,
  fetchSongAudioValidationsEffect,
  fetchSongCountEffect,
  fetchSongDDLEffect,
  fetchSongEffect,
  fetchSongListEffect,
  fetchSongListSearchValidationSchema,
  fetchSongsWithAudioListSearchValidationSchema,
  fetchSongValidationSchemaEffect,
  fetchUsualSuspectsEffect,
  getLyrics2ConnectionEffect,
  inviteCollaboratorsEffect,
  leaveSongEffect,
  postSongAudioEffect,
  removeSongCollaboratorEffect,
  removeSongLatestAudioVersionEffect,
  resendCollaboratorInviteEffect,
  setLyricsSheetVersionEffect,
  setSongLatestAudioVersionEffect,
  updateSongAudioEffect,
  updateSongEffect,
  uploadAudioEffect,
  fetchFolderEffect,
  fetchPinnedSongsEffect,
  orderFolderEffect,
  patchContributionEffect,
} from './effects';
import {
  addOrReplaceAudio,
  createLocalAudio,
  filterAudioReadyForUpload,
  reorderFolders,
  setAudioListWithoutLocalDuplicates,
  setAudioStateToRetryable,
  setAudioStateToTranscodingAndRemoveLocal,
  setAudioStateToUploading,
  SongListState,
  updateAudioState,
  updateLatestAudioVersion,
  updateStateWithAudio,
} from './slice.utils';

export const songListInitialState: SongListState = {
  songs: [],
  count: undefined,
  searchKeyword: undefined,
  collaboratorSearchKeyword: undefined,
  createSongLoadingState: LoadingState.PRISTINE,
  contributionsLoadingState: LoadingState.PRISTINE,
  updatingContributions: false,
  localAudio: {},
  audioListReadyForUpload: [],
  isSettingPrivateAudio: {},
  isFetchingAudio: {},
  isFetchingSongs: true,
  folder: {},
};

const slice = createSlice({
  name: 'songs',
  initialState: songListInitialState,
  reducers: {
    lyricsEditAction: {
      reducer() {},
      prepare(payload, isLyricsSheetEmpty: boolean) {
        return { payload, meta: { isLyricsSheetEmpty } };
      },
    },
    updateSongListSearchAction(
      state,
      { payload }: PayloadAction<string | undefined>
    ) {
      state.searchKeyword = payload;
    },
    updateCollaboratorSearchAction(
      state,
      { payload }: PayloadAction<string | undefined>
    ) {
      state.collaboratorSearchKeyword = payload;
    },
    addSongClickAction() {},
    uploadAudioBlockAction() {},
    updateSongFolderAction(
      state,
      { payload }: PayloadAction<SongFolderChangedPayload>
    ) {
      const folder = state.folder?.items?.find(
        (folder) => folder.guid === payload.guid
      );
      if (folder) {
        folder.title = payload.title;
        folder.songCount = payload.songCount;
      }
    },
    updateSongManuallyAction(
      state,
      { payload }: PayloadAction<Song | undefined>
    ) {
      state.song = state.song ? { ...state.song, ...payload } : payload;
    },
    deleteLocalSongAudioAction(state, { payload }: PayloadAction<Audio>) {
      if (state.song) {
        state.song.audio = state.song.audio?.filter(
          (item) => item.guid !== payload.guid
        );
      }
      if (state.localAudio[payload.parent]) {
        state.localAudio[payload.parent] = state.localAudio[
          payload.parent
        ].filter((item) => item.guid !== payload.guid);
      }
    },
    resetLatestSplitAction(state) {
      state.latestSplit = undefined;
    },
    saveAudioListReadyForUpload(state) {
      const audio = (state.song?.audio || [])
        .filter((audio) =>
          [
            AudioStatusReasonType.READY_FOR_UPLOAD,
            AudioStatusReasonType.UPLOADING,
          ].includes(audio.statusReason)
        )
        .map((audio) => audio.guid);

      state.audioListReadyForUpload = [
        ...state.audioListReadyForUpload,
        ...audio,
      ].filter(onlyUnique);
    },
    updateSongAudioStatus(state, { payload }: PayloadAction<Partial<Audio>>) {
      if (payload.status === AudioStatusType.SUCCESS) {
        state.audioListReadyForUpload = state.audioListReadyForUpload.filter(
          (audioId) => audioId !== payload.guid
        );
      }
      if (
        !state.song?.audio ||
        payload?.status === AudioStatusType.READY_FOR_UPLOAD
      ) {
        return;
      }
      const audioIndex = state.song.audio.findIndex(
        (audio) => audio.guid === payload.guid
      );
      state.song.audio[audioIndex] = {
        ...state.song.audio[audioIndex],
        ...payload,
      };
    },
    updateSongAudioAction(
      state,
      { payload }: PayloadAction<AudioUpdatedPayload>
    ) {
      if (!state?.song?.audio) {
        return;
      }

      if (
        payload.payloadType === 'LatestVersionAudioMarked' &&
        payload.latestVersionAudio
      ) {
        state.song.audio = updateLatestAudioVersion(
          state.song.audio,
          payload.latestVersionAudio
        );
        state.song.latestVersionAudio = payload.latestVersionAudio;
        return;
      }

      if (payload.payloadType === 'LatestVersionAudioDeleted') {
        state.song.latestVersionAudio = undefined;
        state.song.audio = state.song.audio.map((audio) => ({
          ...audio,
          isLatestVersion: false,
        }));
        return;
      }

      if (payload.payloadType === 'AudioDeleted') {
        state.song.audio = state.song.audio.filter(
          (audio) => audio.guid !== payload.audioGuid
        );
        return;
      }

      if (payload.payloadType === 'AudioUpdated') {
        const audioIndex = state.song.audio.findIndex(
          (audio) => audio.guid === payload.audioGuid
        );
        const audio = state.song.audio[audioIndex];

        state.song.audio[audioIndex] = {
          ...audio,
          isPrivate: payload.audio.isPrivate,
          title: payload.audio.title,
          type: payload.audio.type,
          status: payload.audio.status,
          statusReason: payload.audio.statusReason,
          remark: payload.audio.remark || '',
          duration: payload.audio.originalFile.duration,
          fileSize: payload.audio.originalFile.fileSize,
        };
      }
    },
    resetSongCountAction(state) {
      state.count = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSongListEffect.pending, (state) => {
        state.isFetchingSongs = true;
      })
      .addCase(
        fetchSongListEffect.fulfilled,
        (state, { meta, payload: { items, _links } }) => {
          const processedArray = meta.arg.excludeSongGuid
            ? items.filter((song) => song.guid !== meta.arg.excludeSongGuid)
            : items;
          state.songs = meta.arg.next
            ? state.songs.concat(processedArray)
            : processedArray;
          state._links = _links;
          state.isFetchingSongs = false;
        }
      )
      .addCase(fetchSongListEffect.rejected, (state) => {
        state.songs = [];
        state.isFetchingSongs = false;
      })
      .addCase(fetchSongCountEffect.fulfilled, (state, { payload }) => {
        state.count = payload;
      })
      .addCase(createSongEffect.pending, (state) => {
        state.song = undefined;
        state.createSongLoadingState = LoadingState.FETCHING;
      })
      .addCase(createSongEffect.fulfilled, (state, { payload }) => {
        state.createSongLoadingState = LoadingState.FETCHED;
        state.song = payload;
        if (state.count) {
          state.count += 1;
        }
      })
      .addCase(createSongEffect.rejected, (state) => {
        state.createSongLoadingState = LoadingState.ERROR;
      })
      .addCase(fetchSongEffect.fulfilled, (state, { payload }) => {
        state.song = payload;
      })
      .addCase(fetchSongEffect.rejected, (state) => {
        state.song = undefined;
      })
      .addCase(updateSongEffect.fulfilled, (state, { payload }) => {
        state.song = {
          ...state.song,
          ...payload,
          status: state.song?.status || payload.status,
        };
      })
      .addCase(setLyricsSheetVersionEffect.fulfilled, (state, { payload }) => {
        if (!state.song) {
          return;
        }
        state.song = {
          ...state.song,
          ...payload,
        };
      })
      .addCase(leaveSongEffect.fulfilled, (state, { meta }) => {
        state.songs = state.songs.filter((el) => el.guid !== meta.arg.songGuid);
        if (state.count) {
          state.count -= 1;
        }

        // Remove song from folder
        if (state.folder?.song?.items) {
          // Update folders' song count
          const folder = state.folder.items?.find(
            ({ guid }) => state.folder?.item?.guid === guid
          );
          if (folder?.songCount) {
            folder.songCount -= 1;
          }
          state.folder.song.items = state.folder.song.items.filter(
            ({ guid }) => guid !== meta.arg.songGuid
          );
        }
      })
      .addCase(
        fetchSongValidationSchemaEffect.fulfilled,
        (state, { payload }) => {
          state.validations = payload;
        }
      )
      .addCase(fetchLanguageCodesDDLEffect.fulfilled, (state, { payload }) => {
        state.languageCodes = payload;
      })
      .addCase(fetchSongDDLEffect.fulfilled, (state, { payload }) => {
        state.songDDL = payload;
      })
      .addCase(
        fetchCreateContributionValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.createContributionValidation = payload;
        }
      )
      .addCase(fetchContributionsListEffect.pending, (state, { meta }) => {
        if (!meta.arg.update) {
          state.contributionsLoadingState = LoadingState.FETCHING;
        }
        state.updatingContributions = Boolean(meta.arg.update);
      })
      .addCase(fetchContributionsListEffect.fulfilled, (state, { payload }) => {
        if (!state.song) {
          return;
        }
        state.song.contributions = {
          ...state.song.contributions,
          ...payload,
        };
        state.contributionsLoadingState = LoadingState.FETCHED;
        state.updatingContributions = false;
      })
      .addCase(fetchContributionsListEffect.rejected, (state) => {
        if (state.song) {
          state.song.contributions = {};
        }
        state.contributionsLoadingState = LoadingState.ERROR;
        state.updatingContributions = false;
      })
      .addCase(deleteSongContributionEffect.fulfilled, (state, { meta }) => {
        if (!state.song?.contributions?.items) {
          return;
        }
        state.song.contributions.items = state.song.contributions?.items?.filter(
          (el) => el.guid !== meta.arg.guid
        );
      })
      .addCase(
        deleteSongContributorEffect.fulfilled,
        (state, { payload: { guids } }) => {
          if (!state.song?.contributions?.items) {
            return;
          }
          state.song.contributions.items = state.song.contributions?.items?.filter(
            (el) => !guids.includes(el.guid)
          );
        }
      )
      .addCase(postSongAudioEffect.pending, (state, { meta }) => {
        if (meta.arg.audio.retryAudio) {
          setAudioStateToUploading(state, meta.arg.audio.retryAudio);
        }
      })
      .addCase(postSongAudioEffect.fulfilled, (state, { meta, payload }) => {
        const audio = {
          ...payload,
          localFileSize: meta.arg?.audio.file?.size,
        };
        addOrReplaceAudio(state, audio, meta.arg.audio.retryAudio);
      })
      .addCase(postSongAudioEffect.rejected, (state, { meta, payload }) => {
        if (meta.arg.audio.retryAudio) {
          setAudioStateToRetryable(state, meta.arg.audio.retryAudio);
        } else if (payload) {
          createLocalAudio(state, payload);
        }
      })
      .addCase(uploadAudioEffect.pending, (state, { meta }) => {
        setAudioStateToUploading(state, meta.arg.audio);
      })
      .addCase(uploadAudioEffect.fulfilled, (state, { meta }) => {
        setAudioStateToTranscodingAndRemoveLocal(state, meta.arg.audio);
      })
      .addCase(uploadAudioEffect.rejected, (state, { meta }) => {
        setAudioStateToRetryable(state, meta.arg.audio, meta.arg.audioFile);
      })
      .addCase(fetchSongAudioTypesEffect.fulfilled, (state, { payload }) => {
        state.songAudioTypes = payload;
      })
      .addCase(
        fetchSongAudioValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.songAudioValidations = payload;
        }
      )
      .addCase(fetchSongAudioListEffect.pending, (state) => {
        if (state.song) {
          state.song.audio = [];
        }
      })
      .addCase(fetchSongAudioListEffect.fulfilled, (state, { payload }) => {
        if (state.song) {
          const latestAudio = payload.find(
            (audio) => audio.isLatestVersion === true
          );
          state.song.latestVersionAudio = latestAudio?.guid;
        }

        setAudioListWithoutLocalDuplicates(state, payload);
        filterAudioReadyForUpload(state, payload);
      })
      .addCase(fetchSongAudioListEffect.rejected, (state) => {
        if (state.song) {
          state.song.audio = [];
        }
      })
      .addCase(deleteSongAudioEffect.fulfilled, (state, { meta }) => {
        const audioToDelete = meta.arg.audio;
        if (state.song) {
          state.song.audio = state.song.audio?.filter(
            (audio) => audio.guid !== audioToDelete.guid
          );
          if (state.song.latestVersionAudio === audioToDelete.guid) {
            state.song.latestVersionAudio = undefined;
          }
        }
        if (state.localAudio[audioToDelete.parent]) {
          state.localAudio[audioToDelete.parent] = state.localAudio[
            audioToDelete.parent
          ].filter((audio) => audio.guid !== audioToDelete.guid);
        }
        state.audioListReadyForUpload = state.audioListReadyForUpload.filter(
          (audioId) => audioId !== meta.arg.audio.guid
        );
      })
      .addCase(updateSongAudioEffect.pending, (state, { meta }) => {
        if (meta.arg.togglingPrivate) {
          state.isSettingPrivateAudio = {
            ...state.isSettingPrivateAudio,
            [meta.arg.audio.guid]: true,
          };
        }
      })
      .addCase(updateSongAudioEffect.fulfilled, (state, { payload, meta }) => {
        updateAudioState(state, payload, meta.arg.audio);
        if (
          state.song &&
          payload.guid === state.song?.latestVersionAudio &&
          payload.isPrivate
        ) {
          state.song.latestVersionAudio = undefined;
        }
        if (meta.arg.togglingPrivate) {
          state.isSettingPrivateAudio = {
            ...state.isSettingPrivateAudio,
            [meta.arg.audio.guid]: false,
          };
        }
      })
      .addCase(updateSongAudioEffect.rejected, (state, { meta }) => {
        if (meta.arg.togglingPrivate) {
          state.isSettingPrivateAudio = {
            ...state.isSettingPrivateAudio,
            [meta.arg.audio.guid]: false,
          };
        }
      })
      .addCase(fetchSongAudioEffect.pending, (state, { meta }) => {
        state.isFetchingAudio = {
          ...state.isFetchingAudio,
          [meta.arg.guid]: true,
        };
      })
      .addCase(fetchSongAudioEffect.fulfilled, (state, { payload, meta }) => {
        updateStateWithAudio(state, payload, { createLocalAudio: false });
        if (state.isFetchingAudio[meta.arg.guid]) {
          delete state.isFetchingAudio[meta.arg.guid];
        }
      })
      .addCase(inviteCollaboratorsEffect.fulfilled, (state, { payload }) => {
        if (state.song) {
          state.song.collaborators = payload;
        }
      })
      .addCase(getLyrics2ConnectionEffect.fulfilled, (state, { payload }) => {
        if (state.song) {
          state.song.lyricSheet = payload.lyricSheet;
        }
      })
      .addCase(removeSongCollaboratorEffect.fulfilled, (state, { meta }) => {
        if (state.song) {
          state.song.collaborators = state.song?.collaborators?.filter(
            (collaborator) =>
              collaborator.userGuid !== meta.arg.collaborator.userGuid ||
              collaborator.inviteeEmail !== meta.arg.collaborator.inviteeEmail
          );
        }
      })
      .addCase(
        fetchContributionClassesRolesDDLEffect.fulfilled,
        (state, { payload }) => {
          state.contributionClassesAndRolesDDL = payload;
        }
      )
      .addCase(setSongLatestAudioVersionEffect.fulfilled, (state, { meta }) => {
        if (state.song?.audio) {
          state.song.audio = updateLatestAudioVersion(
            state.song.audio,
            meta.arg.audioGuid
          );
          state.song.latestVersionAudio = meta.arg.audioGuid;
        }
      })
      .addCase(
        removeSongLatestAudioVersionEffect.fulfilled,
        (state, { meta }) => {
          if (state.song) {
            const audio = state.song.audio?.find(
              (a) => a.guid === meta.arg.audioGuid
            );
            if (audio) {
              audio.isLatestVersion = false;
            }
            state.song.latestVersionAudio = undefined;
          }
        }
      )
      .addCase(fetchPossibleSplitMembersEffect.pending, (state) => {
        state.possibleSplitMembers = undefined;
      })
      .addCase(
        fetchPossibleSplitMembersEffect.fulfilled,
        (state, { payload }) => {
          const members = payload.map((item) => ({
            roles: item.contributions?.reduce<Member['roles']>(
              (acc = [], contribution) => {
                if (contribution.role) {
                  return [...acc, contribution.role];
                }
                return acc;
              },
              []
            ),
            profile: item.contributor.profile,
            memberIdentifier:
              item.contributor?.profileGuid || item.contributions[0].guid,
          }));
          state.possibleSplitMembers = members;
        }
      )
      .addCase(fetchLatestSplitEffect.pending, (state) => {
        state.latestSplit = undefined;
      })
      .addCase(fetchLatestSplitEffect.fulfilled, (state, { payload }) => {
        const parsedMembers = payload.members.map((member) => ({
          ...member,
          share: Number(member.share ? +member.share / 100 : 0).toFixed(2),
        }));
        const { self, ...splitLinks } = payload._links || {};

        if (state.song) {
          if (!state.song._links) {
            state.song._links = {} as _SongLinks;
          }
          state.song._links = {
            ...state.song._links,
            ...splitLinks,
          };
        }

        state.latestSplit = { ...payload, members: parsedMembers };
      })
      .addCase(createSplitEffect.fulfilled, (state, { payload }) => {
        const parsedMembers = payload.members.map((member) => ({
          ...member,
          share: Number(member.share ? +member.share / 100 : 0).toFixed(2),
        }));
        const { self, ...splitLinks } = payload._links || {};

        if (state.song) {
          if (!state.song._links) {
            state.song._links = {} as _SongLinks;
          }
          state.song._links = {
            ...state.song._links,
            ...splitLinks,
          };
        }

        state.latestSplit = { ...payload, members: parsedMembers };
      })
      .addCase(acceptSplitEffect.fulfilled, (state, { payload }) => {
        const parsedMembers = payload.members.map((member) => ({
          ...member,
          share: Number(member.share ? +member.share / 100 : 0).toFixed(2),
        }));
        state.latestSplit = { ...payload, members: parsedMembers };
      })
      .addCase(
        fetchSongListSearchValidationSchema.fulfilled,
        (state, { payload }) => {
          state.songListValidations = payload;
        }
      )
      .addCase(
        fetchSongsWithAudioListSearchValidationSchema.fulfilled,
        (state, { payload }) => {
          state.songAudioListValidations = payload;
        }
      )
      .addCase(fetchUsualSuspectsEffect.fulfilled, (state, { payload }) => {
        if (state.song) {
          state.song.usualSuspects = payload;
        }
      })
      .addCase(
        resendCollaboratorInviteEffect.fulfilled,
        (state, { payload }) => {
          const reInvitedCollaborator = state.song?.collaborators?.find(
            ({ userGuid }) => userGuid === payload.userGuid
          );

          if (reInvitedCollaborator) {
            delete reInvitedCollaborator._links?.resendInvite;
          }
        }
      )
      .addCase(
        fetchFoldersEffect.fulfilled,
        (state, { payload: { items, ...rest } }) => {
          const allItems = [...(state.folder?.items || []), ...items];
          state.folder = {
            ...state.folder,
            ...rest,
            items: allItems,
          };
        }
      )
      .addCase(orderFolderEffect.pending, (state, { meta }) => {
        const { folderGuid, targetFolderGuid } = meta.arg;

        if (!state.folder?.items) {
          return;
        }

        state.folder.items = reorderFolders({
          items: state.folder.items,
          folderGuid,
          targetFolderGuid,
        });
      })
      .addCase(createFolderEffect.fulfilled, (state, { payload }) => {
        state.folder?.items?.unshift(payload);
      })
      .addCase(updateFolderEffect.fulfilled, (state, { meta, payload }) => {
        const item = state.folder?.items?.find(
          ({ guid }) => guid === payload.guid
        );
        if (item) {
          item.title = payload.title;
          item.songCount = payload.songCount;

          if (state.folder?.song?.items && meta.arg.removeSongs) {
            state.folder.song.items = state.folder.song.items?.filter(
              ({ guid }) => !meta.arg.removeSongs?.includes(guid)
            );
          }
        }
      })
      .addCase(deleteFolderEffect.fulfilled, (state, { meta }) => {
        if (!state.folder?.items) {
          return;
        }

        state.folder.items = state.folder.items.filter(
          ({ guid }) => guid !== meta.arg.guid
        );
      })
      .addCase(fetchFolderEffect.pending, (state) => {
        state.isFetchingSongs = true;
      })
      .addCase(
        fetchFolderEffect.fulfilled,
        (
          state,
          {
            meta,
            payload: {
              folder: { item },
              song,
            },
          }
        ) => {
          state.folder = {
            ...state.folder,
            item,
            song: {
              ...song,
              items: meta.arg.next
                ? (state.folder?.song?.items || []).concat(song.items)
                : song.items,
            },
          };
          state.isFetchingSongs = false;
        }
      )
      .addCase(fetchPinnedSongsEffect.fulfilled, (state, { payload }) => {
        state.pinned = {
          songs: payload.items,
        };
      })
      .addCase(patchContributionEffect.fulfilled, (state, { payload }) => {
        if (!state.song?.contributions?.items || !payload) {
          return;
        }

        const newItems = [...state.song.contributions.items];
        const index = newItems.findIndex(({ guid }) => guid === payload.guid);
        newItems.splice(index, 1, payload);
        state.song.contributions.items = newItems;
      });
  },
});

export const {
  lyricsEditAction,
  updateSongManuallyAction,
  deleteLocalSongAudioAction,
  resetLatestSplitAction,
  updateSongListSearchAction,
  updateCollaboratorSearchAction,
  addSongClickAction,
  saveAudioListReadyForUpload,
  updateSongAudioStatus,
  updateSongAudioAction,
  resetSongCountAction,
  uploadAudioBlockAction,
  updateSongFolderAction,
} = slice.actions;

export const songListReducer = slice.reducer;
